import axios, { AxiosInstance } from 'axios';
import type { DashboardTrendResponse, TrendGranularity, TrendLens } from '../types/dashboardTrend';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only redirect to login if we're not already on a success/cancel page
          // and if the error is not from a profile check (which might be temporary)
          // and if the error is not from a login attempt (which should be handled by the login component)
          // and if we're not on a public route (like /new, /accept-invitation, etc.)
          const currentPath = window.location.pathname;
          const hasSuccessParams = window.location.search.includes('success=') || window.location.search.includes('canceled=');
          const isProfileCheck = error.config?.url?.includes('/auth/profile');
          const isLoginAttempt = error.config?.url?.includes('/auth/login');
          const isPublicRoute = currentPath === '/new' ||
            currentPath.startsWith('/accept-invitation/') ||
            currentPath.startsWith('/reset-password/') ||
            currentPath.startsWith('/verify-email/');
          
          // Don't auto-redirect if we have success parameters, if it's a profile check, if it's a login attempt, or if we're on a public route
          if (!hasSuccessParams && !isProfileCheck && !isLoginAttempt && !isPublicRoute) {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async resendVerification(email: string) {
    const response = await this.api.post('/auth/resend-verification', { email });
    return response.data;
  }

  async registerUser(userData: { email: string; displayName: string; password: string }) {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async setupSystem(userData: { email: string; displayName: string; password: string }) {
    const response = await this.api.post('/auth/setup', userData);
    return response.data;
  }

  async checkSystemSetup() {
    const response = await this.api.get('/auth/setup-status');
    return response.data;
  }

  async getProfile() {
    const response = await this.api.get('/auth/profile');
    return response.data;
  }

  async logout() {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  // Password reset endpoints
  async forgotPassword(email: string) {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string) {
    const response = await this.api.post(`/auth/reset-password/${token}`, { password });
    return response.data;
  }

  async validateResetToken(token: string) {
    const response = await this.api.get(`/auth/reset-password/${token}/validate`);
    return response.data;
  }

  async adminResetUserPassword(userId: string, password: string) {
    const response = await this.api.post(`/auth/admin/reset-password/${userId}`, { password });
    return response.data;
  }

  // Users endpoints
  async getUsers(params?: any) {
    const response = await this.api.get('/users', { params });
    return response.data;
  }

  async getUser(id: string) {
    const response = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(userData: any) {
    const response = await this.api.post('/users', userData);
    return response.data;
  }

  async updateUser(id: string, userData: any) {
    const response = await this.api.put(`/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id: string) {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  // Organizations endpoints
  async getOrganizations(params?: any) {
    const response = await this.api.get('/organizations', { params });
    return response.data;
  }

  async getOrganization(id: string) {
    const response = await this.api.get(`/organizations/${id}`);
    return response.data;
  }

  async createOrganization(orgData: any) {
    const response = await this.api.post('/organizations', orgData);
    return response.data;
  }

  async updateOrganization(id: string, orgData: any) {
    const response = await this.api.put(`/organizations/${id}`, orgData);
    return response.data;
  }

  async deleteOrganization(id: string) {
    const response = await this.api.delete(`/organizations/${id}`);
    return response.data;
  }

  // Companies endpoints
  async getCompanies(params?: any) {
    const response = await this.api.get('/companies', { params });
    return response.data;
  }

  async getCompany(id: string) {
    const response = await this.api.get(`/companies/${id}`);
    return response.data;
  }

  async createCompany(companyData: any) {
    const response = await this.api.post('/companies', companyData);
    return response.data;
  }

  async updateCompany(id: string, companyData: any) {
    const response = await this.api.put(`/companies/${id}`, companyData);
    return response.data;
  }

  async deleteCompany(id: string) {
    const response = await this.api.delete(`/companies/${id}`);
    return response.data;
  }

  // Accounts endpoints
  async getAccounts(params?: any) {
    const response = await this.api.get('/accounts', { params });
    return response.data;
  }

  async getAccount(id: string) {
    const response = await this.api.get(`/accounts/${id}`);
    return response.data;
  }

  async createAccount(accountData: any) {
    const response = await this.api.post('/accounts', accountData);
    return response.data;
  }

  async updateAccount(id: string, accountData: any) {
    const response = await this.api.put(`/accounts/${id}`, accountData);
    return response.data;
  }

  async deleteAccount(id: string) {
    const response = await this.api.delete(`/accounts/${id}`);
    return response.data;
  }

  // Account budgets (BUDGET_SPECIFICATION)
  async getAccountBudget(accountId: string) {
    const response = await this.api.get(`/accounts/${accountId}/budget`);
    return response.data;
  }

  async upsertAccountBudget(accountId: string, body: import('../types/accountBudget').UpsertAccountBudgetBody) {
    const response = await this.api.put(`/accounts/${accountId}/budget`, body);
    return response.data;
  }

  async deleteAccountBudget(accountId: string) {
    const response = await this.api.delete(`/accounts/${accountId}/budget`);
    return response.data;
  }

  async getAccountBudgetProgress(accountId: string) {
    const response = await this.api.get(`/accounts/${accountId}/budget/progress`);
    return response.data;
  }

  async getAccountsBudgetProgress(organizationId: string) {
    const response = await this.api.get('/accounts/budget-progress', {
      params: { organizationId }
    });
    return response.data;
  }

  async getAccountTrend(
    accountId: string,
    params: {
      metric: import('../types/accountTrend').AccountTrendMetric;
      granularity: import('../types/accountTrend').AccountTrendGranularity;
      anchor?: string;
    }
  ) {
    const response = await this.api.get(`/accounts/${accountId}/trend`, { params });
    return response.data;
  }

  async getTaskTrend(
    accountId: string,
    taskId: string,
    params: {
      metric: import('../types/accountTrend').AccountTrendMetric;
      granularity: import('../types/accountTrend').AccountTrendGranularity;
      anchor?: string;
    }
  ) {
    const response = await this.api.get(`/accounts/${accountId}/tasks/${taskId}/trend`, { params });
    return response.data;
  }

  async getAccountTaskEconomy(
    accountId: string,
    params: import('../types/accountTaskEconomy').AccountTaskEconomyParams
  ) {
    const response = await this.api.get(`/accounts/${accountId}/task-economy`, { params });
    return response.data;
  }

  async getAccountUserEconomy(
    accountId: string,
    params: import('../types/accountUserEconomy').AccountUserEconomyParams
  ) {
    const response = await this.api.get(`/accounts/${accountId}/user-economy`, { params });
    return response.data;
  }

  async getTaskUserEconomy(
    accountId: string,
    taskId: string,
    params: import('../types/accountUserEconomy').AccountUserEconomyParams
  ) {
    const response = await this.api.get(`/accounts/${accountId}/tasks/${taskId}/user-economy`, {
      params
    });
    return response.data;
  }

  async getCompanyTrend(
    companyId: string,
    params: {
      metric: import('../types/accountTrend').AccountTrendMetric;
      granularity: import('../types/accountTrend').AccountTrendGranularity;
      anchor?: string;
    }
  ) {
    const response = await this.api.get(`/companies/${companyId}/trend`, { params });
    return response.data;
  }

  async getCompanyAccountEconomy(
    companyId: string,
    params: import('../types/companyAccountEconomy').CompanyAccountEconomyParams
  ) {
    const response = await this.api.get(`/companies/${companyId}/account-economy`, { params });
    return response.data;
  }

  async getCompanyBudgetProgress(companyId: string) {
    const response = await this.api.get(`/companies/${companyId}/budget-progress`);
    return response.data;
  }

  async getOrganizationTrend(
    organizationId: string,
    params: {
      metric: import('../types/accountTrend').AccountTrendMetric;
      granularity: import('../types/accountTrend').AccountTrendGranularity;
      anchor?: string;
    }
  ) {
    const response = await this.api.get(`/organizations/${organizationId}/trend`, { params });
    return response.data;
  }

  async getOrganizationCompanyEconomy(
    organizationId: string,
    params: import('../types/organizationCompanyEconomy').OrganizationCompanyEconomyParams
  ) {
    const response = await this.api.get(`/organizations/${organizationId}/company-economy`, {
      params
    });
    return response.data;
  }

  async getOrganizationBudgetProgress(organizationId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/budget-progress`);
    return response.data;
  }

  async upsertAccountTaskTargets(
    accountId: string,
    targets: Array<{ taskId: string; targetAmount: string | null }>
  ) {
    const response = await this.api.put(`/accounts/${accountId}/budget/task-targets`, { targets });
    return response.data;
  }

  // Tasks endpoints
  async getTasks(params?: any) {
    const response = await this.api.get('/tasks', { params });
    return response.data;
  }

  async getTask(id: string) {
    const response = await this.api.get(`/tasks/${id}`);
    return response.data;
  }

  async createTask(taskData: any) {
    const response = await this.api.post('/tasks', taskData);
    return response.data;
  }

  async updateTask(id: string, taskData: any) {
    const response = await this.api.put(`/tasks/${id}`, taskData);
    return response.data;
  }

  async deleteTask(id: string) {
    const response = await this.api.delete(`/tasks/${id}`);
    return response.data;
  }

  // Timesheet entries endpoints
  async getTimesheetEntries(params?: any) {
    const response = await this.api.get('/timesheet-entries', { params });
    return response.data;
  }

  async getTimesheetEntry(id: string) {
    const response = await this.api.get(`/timesheet-entries/${id}`);
    return response.data;
  }

  async createTimesheetEntry(entryData: any) {
    console.log('🌐 [API DEBUG] Creating timesheet entry:', {
      url: '/timesheet-entries',
      method: 'POST',
      data: entryData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const response = await this.api.post('/timesheet-entries', entryData);
      console.log('✅ [API DEBUG] Timesheet entry created successfully:', {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ [API DEBUG] Timesheet entry creation failed:', {
        error,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  }

  async updateTimesheetEntry(id: string, entryData: any) {
    console.log('🌐 [API DEBUG] Updating timesheet entry:', {
      url: `/timesheet-entries/${id}`,
      method: 'PUT',
      id,
      data: entryData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const response = await this.api.put(`/timesheet-entries/${id}`, entryData);
      console.log('✅ [API DEBUG] Timesheet entry updated successfully:', {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ [API DEBUG] Timesheet entry update failed:', {
        error,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  }

  async deleteTimesheetEntry(id: string) {
    const response = await this.api.delete(`/timesheet-entries/${id}`);
    return response.data;
  }

  async submitTimesheetEntry(id: string, body?: { comments?: string }) {
    const response = await this.api.post(`/timesheet-entries/${id}/submit`, body ?? {});
    return response.data;
  }

  async returnTimesheetEntry(id: string, body?: { reason?: string }) {
    const response = await this.api.post(`/timesheet-entries/${id}/return`, body ?? {});
    return response.data;
  }

  async approveTimesheetEntry(id: string, body?: { comments?: string }) {
    const response = await this.api.post(`/timesheet-entries/${id}/approve`, body ?? {});
    return response.data;
  }

  async rejectTimesheetEntry(id: string, body?: { reason?: string }) {
    const response = await this.api.post(`/timesheet-entries/${id}/reject`, body ?? {});
    return response.data;
  }

  async bulkSubmitTimesheetEntries(body: { entryIds: string[]; comments?: string }) {
    const response = await this.api.post('/timesheet-entries/bulk-submit', body);
    return response.data;
  }

  async bulkReturnTimesheetEntries(body: { entryIds: string[]; reason?: string }) {
    const response = await this.api.post('/timesheet-entries/bulk-return', body);
    return response.data;
  }

  async bulkApproveTimesheetEntries(body: { entryIds: string[]; comments?: string }) {
    const response = await this.api.post('/timesheet-entries/bulk-approve', body);
    return response.data;
  }

  async bulkRejectTimesheetEntries(body: { entryIds: string[]; reason?: string }) {
    const response = await this.api.post('/timesheet-entries/bulk-reject', body);
    return response.data;
  }

  // Plans endpoints
  async getPlans(params?: any) {
    const response = await this.api.get('/plans', { params });
    return response.data;
  }

  // Subscriptions endpoints
  async getSubscriptions(params?: any) {
    const response = await this.api.get('/subscriptions', { params });
    return response.data;
  }

  async getSubscription(organizationId: string) {
    const response = await this.api.get(`/subscriptions?organizationId=${organizationId}`);
    return response.data;
  }

  async getAllSubscriptions() {
    const response = await this.api.get('/subscriptions/admin/all');
    return response.data;
  }

  async createSubscription(subscriptionData: any) {
    const response = await this.api.post('/subscriptions', subscriptionData);
    return response.data;
  }

  async createTrialSubscription(organizationId: string) {
    const response = await this.api.post('/subscriptions/trial', {
      organizationId
    });
    return response.data;
  }

  async createPaidSubscription(organizationId: string, seats: number) {
    const response = await this.api.post('/subscriptions/create-paid', {
      organizationId,
      seats
    });
    return response.data;
  }

  async updateSubscription(id: string, subscriptionData: any) {
    const response = await this.api.put(`/subscriptions/${id}`, subscriptionData);
    return response.data;
  }

  async updateSubscriptionSeats(subscriptionId: string, seats: number) {
    const response = await this.api.put(`/subscriptions/${subscriptionId}/seats`, {
      seats
    });
    return response.data;
  }


  async deleteSubscription(id: string) {
    const response = await this.api.delete(`/subscriptions/${id}`);
    return response.data;
  }

  async cancelSubscription(id: string) {
    const response = await this.api.put(`/subscriptions/${id}`, {
      status: 'canceled'
    });
    return response.data;
  }

  // License usage endpoints
  async getLicenseUsage(params?: any) {
    const response = await this.api.get('/license-usage', { params });
    return response.data;
  }

  async updateLicenseUsage(id: string, usageData: any) {
    const response = await this.api.put(`/license-usage/${id}`, usageData);
    return response.data;
  }

  // User organizations endpoints
  async getUserOrganizations(params?: any) {
    const response = await this.api.get('/user-organizations', { params });
    return response.data;
  }

  async createUserOrganization(userOrgData: any) {
    const response = await this.api.post('/user-organizations', userOrgData);
    return response.data;
  }

  async updateUserOrganization(userId: string, orgId: string, userOrgData: any) {
    const response = await this.api.put(`/user-organizations/${userId}/${orgId}`, userOrgData);
    return response.data;
  }

  async deleteUserOrganization(userId: string, orgId: string) {
    const response = await this.api.delete(`/user-organizations/${userId}/${orgId}`);
    return response.data;
  }

  // Organization-specific endpoints (with RLS)
  async getOrganizationUsers(organizationId: string, params?: any) {
    const response = await this.api.get(`/organizations/${organizationId}/users`, { params });
    return response.data;
  }

  async putOrganizationMember(organizationId: string, userId: string, body: { role: string; isActive?: boolean }) {
    const response = await this.api.put(`/organizations/${organizationId}/members/${userId}`, body);
    return response.data;
  }

  async getOrganizationSettings(organizationId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/settings`);
    return response.data;
  }

  async updateOrganizationSettings(organizationId: string, settings: any) {
    const response = await this.api.put(`/organizations/${organizationId}/settings`, settings);
    return response.data;
  }

  async getOrganizationTeam(organizationId: string, params?: any) {
    const response = await this.api.get(`/organizations/${organizationId}/team`, { params });
    return response.data;
  }

  async inviteUserToOrganization(invitationData: { organizationId: string; email: string; role: string }) {
    const response = await this.api.post('/invitations/send', invitationData);
    return response.data;
  }

  async getOrganizationInvitations(organizationId: string, params?: any) {
    const response = await this.api.get(`/invitations/organization/${organizationId}`, { params });
    return response.data;
  }

  async deleteOrganizationInvitation(invitationId: string) {
    const response = await this.api.delete(`/invitations/${invitationId}`);
    return response.data;
  }

  // Pivot table reporting endpoints
  async getPivotTableReport(requestData: {
    levels: string[];
    dateFrom: string;
    dateTo: string;
    organizationId: string;
    timezone?: string;
    filters?: any;
  }) {
    const response = await this.api.post('/pivot-table', requestData);
    return response.data;
  }

  async validateInvitation(token: string) {
    const response = await this.api.get(`/invitations/validate/${token}`);
    return response.data;
  }

  async acceptInvitation(token: string, data: { password?: string; displayName?: string }) {
    const response = await this.api.post(`/invitations/accept/${token}`, data);
    return response.data;
  }

  async checkUserExists(email: string) {
    const response = await this.api.get(`/users/check/${encodeURIComponent(email)}`);
    return response.data;
  }

  // User company roles endpoints
  async getUserCompanyRoles(params?: any) {
    const response = await this.api.get('/user-company-roles', { params });
    return response.data;
  }

  async createUserCompanyRole(userCompanyRoleData: any) {
    const response = await this.api.post('/user-company-roles', userCompanyRoleData);
    return response.data;
  }

  async updateUserCompanyRole(userId: string, companyId: string, userCompanyRoleData: any) {
    const response = await this.api.put(`/user-company-roles/${userId}/${companyId}`, userCompanyRoleData);
    return response.data;
  }

  async deleteUserCompanyRole(userId: string, companyId: string) {
    const response = await this.api.delete(`/user-company-roles/${userId}/${companyId}`);
    return response.data;
  }

  // Company user management endpoints
  async getCompanyUsers(companyId: string) {
    const response = await this.api.get(`/companies/${companyId}/users`);
    return response.data;
  }

  async assignUserToCompany(companyId: string, userData: { userId: string; role: string }) {
    const response = await this.api.post(`/companies/${companyId}/users`, userData);
    return response.data;
  }

  async updateUserCompanyRoleViaCompany(companyId: string, userId: string, role: string) {
    const response = await this.api.put(`/companies/${companyId}/users/${userId}`, { role });
    return response.data;
  }

  async removeUserFromCompany(companyId: string, userId: string) {
    const response = await this.api.delete(`/companies/${companyId}/users/${userId}`);
    return response.data;
  }

  // User account roles endpoints
  async getUserAccountRoles(params?: any) {
    const response = await this.api.get('/user-account-roles', { params });
    return response.data;
  }

  async createUserAccountRole(userAccountRoleData: any) {
    const response = await this.api.post('/user-account-roles', userAccountRoleData);
    return response.data;
  }

  async updateUserAccountRole(userId: string, accountId: string, userAccountRoleData: any) {
    const response = await this.api.put(`/user-account-roles/${userId}/${accountId}`, userAccountRoleData);
    return response.data;
  }

  async deleteUserAccountRole(userId: string, accountId: string) {
    const response = await this.api.delete(`/user-account-roles/${userId}/${accountId}`);
    return response.data;
  }

  // Account user management endpoints
  async getAccountUsers(accountId: string) {
    const response = await this.api.get(`/accounts/${accountId}/users`);
    return response.data;
  }

  async assignUserToAccount(accountId: string, userData: { userId: string; role: string }) {
    const response = await this.api.post(`/accounts/${accountId}/users`, userData);
    return response.data;
  }

  async updateUserAccountRoleViaAccount(accountId: string, userId: string, role: string) {
    const response = await this.api.put(`/accounts/${accountId}/users/${userId}`, { role });
    return response.data;
  }

  async removeUserFromAccount(accountId: string, userId: string) {
    const response = await this.api.delete(`/accounts/${accountId}/users/${userId}`);
    return response.data;
  }

  // User preferences endpoints
  async getUserPreferences() {
    const response = await this.api.get('/preferences');
    return response.data;
  }

  async getUserPreference(key: string, organizationId?: string) {
    const params = organizationId ? { organizationId } : {};
    const response = await this.api.get(`/preferences/${key}`, { params });
    return response.data;
  }

  async updateUserPreference(key: string, value: any, type: string = 'string') {
    const data: any = { value, type };
    const response = await this.api.put(`/preferences/${key}`, data);
    return response.data;
  }

  async deleteUserPreference(key: string) {
    const response = await this.api.delete(`/preferences/${key}`);
    return response.data;
  }

  // Reports endpoints
  async getUsageReport(params?: any) {
    const response = await this.api.get('/reports/usage', { params });
    return response.data;
  }

  async getTimesheetReport(params?: any) {
    const response = await this.api.get('/reports/timesheets', { params });
    return response.data;
  }

  async getBillingReport(params?: any) {
    const response = await this.api.get('/reports/billing', { params });
    return response.data;
  }

  async getDashboardTrend(params: {
    lens: TrendLens;
    granularity: TrendGranularity;
    anchor: string;
    timezone: string;
    organizationId?: string;
  }): Promise<{ success: boolean; data: DashboardTrendResponse }> {
    const response = await this.api.get('/dashboard/trend', { params });
    return response.data;
  }

  async getOrganizationTransactions(organizationId: string) {
    const response = await this.api.get(`/subscriptions/organizations/${organizationId}/transactions`);
    return response.data;
  }

  async syncSubscriptionFromStripe(organizationId: string) {
    const response = await this.api.post('/subscriptions/sync-from-stripe', { organizationId });
    return response.data;
  }

  // Admin email service (platform admin only)
  async getEmailServiceStatus() {
    const response = await this.api.get('/admin/email-service/status');
    return response.data;
  }

  async sendTestEmail(email: string) {
    const response = await this.api.post('/admin/email-service/send-test', { email });
    return response.data;
  }

  // Metrics endpoints
  async getMetricsJobStatus() {
    const response = await this.api.get('/analytics/jobs/status');
    return response.data;
  }

  async triggerMetrics15MinJob() {
    const response = await this.api.post('/analytics/jobs/15min/trigger');
    return response.data;
  }

  async triggerMetricsDailyJob() {
    const response = await this.api.post('/analytics/jobs/daily/trigger');
    return response.data;
  }

  async triggerMetricsBackfillJob(startDate: string, endDate: string) {
    const response = await this.api.post('/analytics/jobs/backfill/trigger', {
      startDate,
      endDate
    });
    return response.data;
  }

  async cleanupMetricsSheet() {
    const response = await this.api.post('/analytics/sheet/cleanup');
    return response.data;
  }

  // Platform reference FX (PLATFORM_ADMIN)
  async getPlatformFx() {
    const response = await this.api.get('/platform/fx');
    return response.data;
  }

  async putPlatformFxRates(rates: { quoteCurrency: string; rateVsEur: string; manualOverride?: boolean }[]) {
    const response = await this.api.put('/platform/fx/rates', { rates });
    return response.data;
  }

  async syncPlatformFx() {
    const response = await this.api.post('/platform/fx/sync');
    return response.data;
  }

  // Organization currency (org OWNER/MANAGER)
  async getOrganizationCurrency(organizationId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/currency`);
    return response.data;
  }

  /** Default + additional codes for entity currency pickers (any org member). */
  async getOrganizationCurrencyCurated(organizationId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/currency/curated`);
    return response.data;
  }

  async patchOrganizationDefaultCurrency(organizationId: string, defaultCurrency: string) {
    const response = await this.api.patch(`/organizations/${organizationId}/currency`, { defaultCurrency });
    return response.data;
  }

  async addOrganizationAdditionalCurrency(organizationId: string, currencyCode: string) {
    const response = await this.api.post(`/organizations/${organizationId}/currency/additional`, { currencyCode });
    return response.data;
  }

  async removeOrganizationAdditionalCurrency(organizationId: string, currencyCode: string, force?: boolean) {
    const response = await this.api.delete(`/organizations/${organizationId}/currency/additional/${currencyCode}`, {
      params: force ? { force: 'true' } : undefined
    });
    return response.data;
  }

  async patchOrganizationAdditionalFollow(organizationId: string, currencyCode: string, followPlatformRate: boolean) {
    const response = await this.api.patch(
      `/organizations/${organizationId}/currency/additional/${currencyCode}/follow`,
      { followPlatformRate }
    );
    return response.data;
  }

  async postOrganizationPullPlatformRate(organizationId: string, currencyCode: string) {
    const response = await this.api.post(
      `/organizations/${organizationId}/currency/additional/${currencyCode}/pull-platform`
    );
    return response.data;
  }

  async getOrganizationCurrencyHistory(organizationId: string, segment: string) {
    const response = await this.api.get(`/organizations/${organizationId}/currency/history/${segment}`);
    return response.data;
  }

  async getOrganizationFxOverrides(organizationId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/currency/overrides`);
    return response.data;
  }

  async postOrganizationFxOverride(
    organizationId: string,
    body: { otherCurrency: string; defaultPer1Other: string; effectiveFrom: string; effectiveTo?: string | null }
  ) {
    const response = await this.api.post(`/organizations/${organizationId}/currency/overrides`, body);
    return response.data;
  }

  // Organization default & member hourly rates (EMPLOYEE_COST, BILLING) — org OWNER/MANAGER
  async getOrganizationHourlyRates(organizationId: string, kind?: 'EMPLOYEE_COST' | 'BILLING') {
    const response = await this.api.get(`/organizations/${organizationId}/hourly-rates/organization`, {
      params: kind ? { kind } : undefined
    });
    return response.data;
  }

  async createOrganizationHourlyRate(
    organizationId: string,
    body: { kind: 'EMPLOYEE_COST' | 'BILLING'; effectiveFrom: string; amount: string; currencyCode: string }
  ) {
    const response = await this.api.post(`/organizations/${organizationId}/hourly-rates/organization`, body);
    return response.data;
  }

  async patchOrganizationHourlyRate(
    organizationId: string,
    rowId: string,
    body: Partial<{ effectiveFrom: string; amount: string; currencyCode: string }>
  ) {
    const response = await this.api.patch(`/organizations/${organizationId}/hourly-rates/organization/${rowId}`, body);
    return response.data;
  }

  async deleteOrganizationHourlyRate(organizationId: string, rowId: string) {
    const response = await this.api.delete(`/organizations/${organizationId}/hourly-rates/organization/${rowId}`);
    return response.data;
  }

  async getMemberHourlyRates(organizationId: string, userId: string, kind?: 'EMPLOYEE_COST' | 'BILLING') {
    const response = await this.api.get(`/organizations/${organizationId}/hourly-rates/members/${userId}`, {
      params: kind ? { kind } : undefined
    });
    return response.data;
  }

  async createMemberHourlyRate(
    organizationId: string,
    userId: string,
    body: { kind: 'EMPLOYEE_COST' | 'BILLING'; effectiveFrom: string; amount: string; currencyCode: string }
  ) {
    const response = await this.api.post(`/organizations/${organizationId}/hourly-rates/members/${userId}`, body);
    return response.data;
  }

  async patchMemberHourlyRate(
    organizationId: string,
    userId: string,
    rowId: string,
    body: Partial<{ effectiveFrom: string; amount: string; currencyCode: string }>
  ) {
    const response = await this.api.patch(
      `/organizations/${organizationId}/hourly-rates/members/${userId}/${rowId}`,
      body
    );
    return response.data;
  }

  async deleteMemberHourlyRate(organizationId: string, userId: string, rowId: string) {
    const response = await this.api.delete(`/organizations/${organizationId}/hourly-rates/members/${userId}/${rowId}`);
    return response.data;
  }

  // Working calendars (org library + per-member assignment, FTE, personal rules, resolve)
  async listWorkingCalendars(organizationId: string, includeArchived?: boolean) {
    const response = await this.api.get(`/organizations/${organizationId}/working-calendars`, {
      params: includeArchived ? { includeArchived: 'true' } : undefined
    });
    return response.data;
  }

  async getWorkingCalendarDefault(organizationId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/working-calendars/organization/default`);
    return response.data;
  }

  async patchWorkingCalendarDefault(organizationId: string, workingCalendarId: string | null) {
    const response = await this.api.patch(`/organizations/${organizationId}/working-calendars/organization/default`, {
      workingCalendarId
    });
    return response.data;
  }

  async createWorkingCalendar(
    organizationId: string,
    body: {
      name: string;
      firstDayOfWeek?: number;
      rules?: Array<{ sortOrder: number; ruleType: string; payload: unknown }>;
    }
  ) {
    const response = await this.api.post(`/organizations/${organizationId}/working-calendars`, body);
    return response.data;
  }

  async getWorkingCalendar(organizationId: string, calendarId: string) {
    const response = await this.api.get(`/organizations/${organizationId}/working-calendars/${calendarId}`);
    return response.data;
  }

  async patchWorkingCalendar(
    organizationId: string,
    calendarId: string,
    body: Partial<{ name: string; archived: boolean; firstDayOfWeek: number }>
  ) {
    const response = await this.api.patch(`/organizations/${organizationId}/working-calendars/${calendarId}`, body);
    return response.data;
  }

  async putWorkingCalendarRules(
    organizationId: string,
    calendarId: string,
    rules: Array<{ sortOrder: number; ruleType: string; payload: unknown }>
  ) {
    const response = await this.api.put(`/organizations/${organizationId}/working-calendars/${calendarId}/rules`, {
      rules
    });
    return response.data;
  }

  async deleteWorkingCalendar(organizationId: string, calendarId: string) {
    const response = await this.api.delete(`/organizations/${organizationId}/working-calendars/${calendarId}`);
    return response.data;
  }

  async getWorkingCalendarPreview(
    organizationId: string,
    calendarId: string,
    params: { from: string; to: string; timezone: string }
  ) {
    const response = await this.api.get(`/organizations/${organizationId}/working-calendars/${calendarId}/preview`, {
      params
    });
    return response.data;
  }

  async listMemberWorkingCalendarAssignments(organizationId: string, userId: string) {
    const response = await this.api.get(
      `/organizations/${organizationId}/working-calendars/members/${userId}/assignments`
    );
    return response.data;
  }

  async createMemberWorkingCalendarAssignment(
    organizationId: string,
    userId: string,
    body: { workingCalendarId: string; effectiveFrom: string }
  ) {
    const response = await this.api.post(
      `/organizations/${organizationId}/working-calendars/members/${userId}/assignments`,
      body
    );
    return response.data;
  }

  async deleteMemberWorkingCalendarAssignment(
    organizationId: string,
    userId: string,
    assignmentId: string
  ) {
    const response = await this.api.delete(
      `/organizations/${organizationId}/working-calendars/members/${userId}/assignments/${assignmentId}`
    );
    return response.data;
  }

  async listMemberEmploymentScales(organizationId: string, userId: string) {
    const response = await this.api.get(
      `/organizations/${organizationId}/working-calendars/members/${userId}/employment-scales`
    );
    return response.data;
  }

  async createMemberEmploymentScale(
    organizationId: string,
    userId: string,
    body: { effectiveFrom: string; factor: string }
  ) {
    const response = await this.api.post(
      `/organizations/${organizationId}/working-calendars/members/${userId}/employment-scales`,
      body
    );
    return response.data;
  }

  async deleteMemberEmploymentScale(organizationId: string, userId: string, rowId: string) {
    const response = await this.api.delete(
      `/organizations/${organizationId}/working-calendars/members/${userId}/employment-scales/${rowId}`
    );
    return response.data;
  }

  async getMemberPersonalWorkingRules(organizationId: string, userId: string) {
    const response = await this.api.get(
      `/organizations/${organizationId}/working-calendars/members/${userId}/personal-rules`
    );
    return response.data;
  }

  async putMemberPersonalWorkingRules(
    organizationId: string,
    userId: string,
    rules: Array<{ sortOrder: number; ruleType: string; payload: unknown }>
  ) {
    const response = await this.api.put(
      `/organizations/${organizationId}/working-calendars/members/${userId}/personal-rules`,
      { rules }
    );
    return response.data;
  }

  async bulkAssignWorkingCalendar(
    organizationId: string,
    body: { userIds: string[]; workingCalendarId: string; effectiveFrom: string }
  ) {
    const response = await this.api.post(
      `/organizations/${organizationId}/working-calendars/members/bulk-assign`,
      body
    );
    return response.data;
  }

  async resolveWorkingCalendar(
    organizationId: string,
    params: { userId: string; from: string; to: string; timezone: string }
  ) {
    const response = await this.api.get(`/organizations/${organizationId}/working-calendars/resolve`, { params });
    return response.data;
  }

  // Generic HTTP methods for Issues API
  async get(url: string, config?: any) {
    const response = await this.api.get(url, config);
    return response;
  }

  async post(url: string, data?: any, config?: any) {
    const response = await this.api.post(url, data, config);
    return response;
  }

  async put(url: string, data?: any, config?: any) {
    const response = await this.api.put(url, data, config);
    return response;
  }

  async delete(url: string, config?: any) {
    const response = await this.api.delete(url, config);
    return response;
  }

}

const apiService = new ApiService();
export default apiService; 